import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ClipboardCheck, Mail, Phone, PhoneOff, MailX, Send, ChevronRight, RefreshCw, Info, FileText, AlertTriangle } from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoHint } from "@/components/InfoHint";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function monthOptions() {
  const now = new Date();
  const out = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${MESI[d.getMonth()]} ${d.getFullYear()}` });
  }
  return out;
}

const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
};
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const COLS = [
  { key: "inoltrati", label: "Inoltrati", cls: "font-bold text-blue-700", help: "Totale lead inoltrati dal router (dem@italiacorsi.it) a questo commerciale nel periodo." },
  { key: "ricevuti_comm", label: "Da comm.", cls: "font-bold text-fuchsia-700", help: "Lead che un ALTRO commerciale ha girato a questo commerciale (ora di sua proprietà su Pipedrive)." },
  { key: "da_gestire", label: "Da gestire", cls: "font-bold text-violet-700", help: "Lead che deve gestire: quelli inoltrati a lui + quelli ricevuti da altri commerciali. Esclude i lead che ha girato ad altri." },
  { key: "gestiti", label: "Gestiti", cls: "text-emerald-700", help: "Lead presi in carico: contattati via email e/o telefonata DOPO l'inoltro." },
  { key: "solo_email", label: "Solo email", cls: "text-sky-700", help: "Gestiti contattati SOLO via email (nessuna chiamata dopo l'inoltro)." },
  { key: "solo_chiamata", label: "Solo chiamata", cls: "text-indigo-700", help: "Gestiti contattati SOLO con chiamata (nessuna email dopo l'inoltro)." },
  { key: "email_e_chiamata", label: "Email+chiamata", cls: "text-teal-700", help: "Gestiti contattati SIA via email SIA con chiamata dopo l'inoltro." },
  { key: "solo_attivita", label: "Attività pianif.", cls: "text-cyan-700", help: "Presi in carico tramite un'attività Pipedrive (es. promemoria/callback) pianificata DOPO l'inoltro, anche senza email o chiamata." },
  { key: "reforwarded", label: "Ad altro comm.", cls: "text-amber-700", help: "Lead che questo commerciale ha girato a un ALTRO commerciale (compaiono come 'Da comm.' per chi li riceve)." },
  { key: "in_attesa", label: "In attesa (<24h)", cls: "text-amber-600", help: "Lead inoltrati da meno di 24 ore e non ancora contattati: c'è ancora tempo per gestirli, non contano come 'mai sentiti'." },
  { key: "mai_sentito", label: "Mai sentiti", cls: "text-rose-700 font-semibold", help: "Lead senza contatto (né chiamata né email) né attività Pipedrive pianificata, da OLTRE 24 ore dall'inoltro." },
];

const CAT_LABEL = {
  inoltrati: "Tutti i lead inoltrati",
  ricevuti_comm: "Ricevuti da altri commerciali",
  da_gestire: "Lead da gestire (inclusi quelli ricevuti da altri commerciali)",
  gestiti: "Lead gestiti",
  solo_email: "Gestiti — solo email",
  solo_chiamata: "Gestiti — solo chiamata",
  email_e_chiamata: "Gestiti — email + chiamata",
  solo_attivita: "Presi in carico — attività Pipedrive pianificata",
  reforwarded: "Inoltrati ad altro commerciale",
  in_attesa: "In attesa (<24h dall'inoltro)",
  mai_sentito: "Mai sentiti (oltre 24h senza contatto né attività)",
};

function CellButton({ value, cls, onClick }) {
  if (!value) return <span className="text-muted-foreground">0</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer rounded px-1.5 underline decoration-dotted underline-offset-2 hover:bg-secondary ${cls}`}
      title="Vedi i lead"
    >
      {value}
    </button>
  );
}

function StatTable({ rows, scope, onCell }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs font-semibold uppercase tracking-wider">Commerciale</TableHead>
            {COLS.map((c) => (
              <TableHead key={c.key} className="text-center text-xs font-semibold uppercase tracking-wider">
                <span className="inline-flex items-center justify-center gap-1">
                  {c.label}
                  <InfoHint testid={`col-info-${c.key}`} label={`Info ${c.label}`} text={c.help} iconClass="h-3.5 w-3.5" className="text-muted-foreground/60" />
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.commercial} data-testid={`handling-row-${r.commercial}`}>
              <TableCell className="font-medium">{r.commercial}</TableCell>
              {COLS.map((c) => (
                <TableCell key={c.key} className="text-center">
                  <CellButton value={r[c.key]} cls={c.cls} onClick={() => onCell(r.commercial, c.key, scope)} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ActivityBadges({ l }) {
  if (l.category === "reforwarded") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
        <Send className="h-3 w-3" /> → {l.owner_name || "altro"}
      </span>
    );
  }
  const badges = [];
  if (l.received_from) badges.push(
    <span key="rf" className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-semibold text-fuchsia-700" title={`Ricevuto da ${l.received_from}`}>
      <Send className="h-3 w-3" /> da {l.received_from}
    </span>
  );
  if (l.has_call) badges.push(
    <span key="c" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${l.matched_secondary ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} title={l.last_agent ? `Ultima: ${l.last_agent} · ${fmtDateTime(l.last_call_at)}` : ""}>
      <Phone className="h-3 w-3" /> {l.matched_secondary ? "Chiamato · n. Pipedrive" : "Chiamato"}{l.calls_total > 1 ? ` (${l.calls_total})` : ""}
    </span>
  );
  if (l.has_proposal) badges.push(
    <span key="p" className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700" title="Proposta inviata dal gestionale (vale come email → gestito)"><FileText className="h-3 w-3" /> Proposta</span>
  );
  if (l.has_email && !l.has_proposal) badges.push(
    <span key="e" className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700"><Mail className="h-3 w-3" /> Email</span>
  );
  if (!l.has_call && !l.has_email && !l.has_proposal) badges.push(
    <span key="n" className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"><PhoneOff className="h-3 w-3" /> Mai sentito</span>
  );
  return <div className="flex flex-wrap gap-1">{badges}</div>;
}

export default function HandlingReport() {
  const navigate = useNavigate();
  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].value);
  const [sel, setSel] = useState(null); // {commercial, category, scope}

  const { data, isLoading } = useQuery({
    queryKey: ["handling-report", month],
    queryFn: async () => (await api.get("/reports/handling", { params: { month } })).data,
  });

  const onCell = (commercial, category, scope) => setSel({ commercial, category, scope });

  const { data: detail, isLoading: detailLoading } = useQuery({
    enabled: !!sel,
    queryKey: ["handling-leads", sel],
    queryFn: async () => {
      const params = { commercial: sel.commercial, category: sel.category };
      if (sel.scope.date) params.date = sel.scope.date; else params.month = month;
      return (await api.get("/reports/handling/leads", { params })).data;
    },
  });

  const sendReminder = useMutation({
    mutationFn: async () => {
      const body = { commercial: sel.commercial, category: sel.category };
      if (sel.scope.date) body.date = sel.scope.date; else body.month = month;
      return (await api.post("/reports/handling/send-reminder", body)).data;
    },
    onSuccess: (d) => toast.success(`Email inviata a ${d.to} · ${d.count} lead da sentire`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Invio email non riuscito"),
  });

  const qc = useQueryClient();
  const [recheckingId, setRecheckingId] = useState(null);

  const recheckLead = async (l) => {
    setRecheckingId(l.id);
    try {
      const body = { lead_id: l.id, commercial: sel.commercial, category: sel.category };
      if (sel.scope.date) body.date = sel.scope.date; else body.month = month;
      const d = (await api.post("/reports/handling/recheck-lead", body)).data;
      if (d.row) {
        qc.setQueryData(["handling-leads", sel], (old) => {
          if (!old) return old;
          return { ...old, leads: old.leads.map((x) => (x.id === l.id ? { ...x, ...d.row } : x)) };
        });
      }
      if (d.resolved) {
        toast.success(`${l.name || l.email || "Lead"} ora risulta gestito`);
        qc.invalidateQueries({ queryKey: ["handling-report"] });
      } else {
        toast.info("Nessun nuovo contatto: ancora da sentire");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ricontrollo non riuscito");
    } finally {
      setRecheckingId(null);
    }
  };

  const refreshLeads = useMutation({
    mutationFn: async () => {
      const body = { commercial: sel.commercial, category: sel.category };
      if (sel.scope.date) body.date = sel.scope.date; else body.month = month;
      return (await api.post("/reports/handling/refresh", body)).data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["handling-leads"] });
      qc.invalidateQueries({ queryKey: ["handling-report"] });
      toast.success(`Aggiornato · ${d.resolved_count} gestiti dopo la mail · ${d.count} ancora da sentire`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Aggiornamento non riuscito"),
  });

  return (
    <div className="space-y-8" data-testid="handling-report-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Resoconto di come i lead inoltrati (da dem@italiacorsi.it) vengono gestiti da ogni commerciale, così nessun contatto viene tralasciato.
          Ogni mattina (lun–sab, ore 08:00) ne arriva una copia via email ad a.ricci@italiacorsi.it. <b>Clicca su un numero</b> per vedere i lead e le attività.
        </p>
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[200px]" data-testid="report-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="shadow-sm" data-testid="handling-month-totals">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <ClipboardCheck className="h-4 w-4 text-blue-600" /> Riepilogo mese — {data?.month_label || ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : data?.by_commercial?.length ? (
            <StatTable rows={data.by_commercial} scope={{ month }} onCell={onCell} />
          ) : (
            <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">Nessun lead inoltrato in questo mese.</div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h3 className="font-display text-base font-semibold tracking-tight">Giorno per giorno</h3>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : data?.by_day?.length ? (
          data.by_day.map((d) => (
            <Card key={d.date} className="shadow-sm" data-testid={`handling-day-${d.date}`}>
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-sm font-semibold capitalize tracking-tight">{fmtDay(d.date)}</CardTitle>
              </CardHeader>
              <CardContent>
                <StatTable rows={d.commercials} scope={{ date: d.date }} onCell={onCell} />
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">Nessun dato per il mese selezionato.</div>
        )}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="handling-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {sel?.commercial} — {CAT_LABEL[sel?.category] || sel?.category}
              {sel?.scope?.date ? <span className="ml-1 font-normal capitalize text-muted-foreground">· {fmtDay(sel.scope.date)}</span> : <span className="ml-1 font-normal text-muted-foreground">· {data?.month_label}</span>}
            </DialogTitle>
          </DialogHeader>
          {sel?.category === "mai_sentito" && detail?.leads?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="send-reminder-btn"
                onClick={() => sendReminder.mutate()}
                disabled={sendReminder.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-700 disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {sendReminder.isPending
                  ? "Invio in corso…"
                  : `Invia a ${sel.commercial} i ${detail.count} lead da sentire`}
              </button>
              <button
                type="button"
                data-testid="refresh-leads-btn"
                onClick={() => refreshLeads.mutate()}
                disabled={refreshLeads.isPending}
                title="Aggiorna lo stato: ricontrolla chiamate ed email fatte dopo il sollecito (lo storico viene sempre salvato)"
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-secondary disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${refreshLeads.isPending ? "animate-spin" : ""}`} />
                {refreshLeads.isPending ? "Aggiorno…" : "Aggiorna stato"}
              </button>
            </div>
          )}
          {detailLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : detail?.leads?.length ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{detail.count} lead</p>
              {detail.leads.map((l) => (
                <div
                  key={l.id}
                  data-testid={`detail-lead-${l.id}`}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border border-border p-3 text-left"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/leads/${l.id}`)}
                    className="min-w-0 flex-1 rounded transition-colors hover:bg-secondary/50"
                  >
                    <p className="truncate text-sm font-medium">{l.name || l.email || "—"}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {l.course || "Corso non riconosciuto"}{l.phone ? ` · ${l.phone}` : ""}{l.email ? ` · ${l.email}` : ""}
                    </p>
                    <p className="text-[11px] text-muted-foreground">Inoltrato il {fmtDateTime(l.assigned_at)}</p>
                  </button>
                  <div className="flex shrink-0 items-center gap-2">
                    {l.pd_ambiguous && (
                      <span
                        data-testid={`pd-ambiguous-${l.id}`}
                        title="Non collegato a Pipedrive: esistono più schede con questo stesso nome. Verifica a mano quale sia il contatto giusto (note/attività)."
                        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"
                      >
                        <AlertTriangle className="h-3 w-3" /> Omonimi Pipedrive
                      </span>
                    )}
                    <ActivityBadges l={l} />
                    <button
                      type="button"
                      data-testid={`recheck-lead-${l.id}`}
                      onClick={() => recheckLead(l)}
                      disabled={recheckingId === l.id}
                      title="Ricontrolla in tempo reale chiamate, email e proposte di questo lead"
                      className="inline-flex items-center justify-center rounded-md border border-border bg-white p-1.5 text-slate-600 transition-colors hover:bg-secondary disabled:opacity-60"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${recheckingId === l.id ? "animate-spin" : ""}`} />
                    </button>
                    <button type="button" onClick={() => navigate(`/leads/${l.id}`)} title="Apri scheda lead" className="text-muted-foreground">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">Nessun lead in questa categoria.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
